import React, { useState, useEffect } from 'react';
import { AiOutlineLeft, AiOutlineRight, AiOutlineDoubleLeft, AiOutlineDoubleRight } from 'react-icons/ai';

function PaginationComponent({ currentPage, totalPages, onPageChange, totalItems = 0, perPage = 15 }) {
  const [maxVisiblePages, setMaxVisiblePages] = useState(3);

  useEffect(() => {
    const updateMaxVisiblePages = () => {
      if (window.innerWidth >= 1024) {
        setMaxVisiblePages(7);
      } else if (window.innerWidth >= 768) {
        setMaxVisiblePages(5);
      } else {
        setMaxVisiblePages(3);
      }
    };

    updateMaxVisiblePages();
    window.addEventListener('resize', updateMaxVisiblePages);

    return () => {
      window.removeEventListener('resize', updateMaxVisiblePages);
    };
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    onPageChange(newPage);
  };

  const generatePageNumbers = () => {
    const pageNumbers = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const pageNumbers = generatePageNumbers();
  const startItem = (currentPage - 1) * perPage + 1;
  const endItem = Math.min(currentPage * perPage, totalItems);

  return (
    <div className="flex flex-col items-center gap-4 py-6 px-4">
      {/* Info text */}
      <div className="text-sm text-gray-400">
        <span className="text-white-100 font-medium">{startItem}</span>
        {' - '}
        <span className="text-white-100 font-medium">{endItem}</span>
        {' / '}
        <span className="text-white-100 font-medium">{totalItems}</span>
        {' kayıt gösteriliyor'}
      </div>

      {/* Pagination controls */}
      <div className="flex items-center gap-2">
        {/* First page button */}
        <button
          className={`px-3 py-2 rounded-lg transition-all ${
            currentPage <= 1
              ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
              : 'bg-primary text-white-100 hover:bg-opacity-80'
          }`}
          onClick={() => handlePageChange(1)}
          disabled={currentPage <= 1}
          aria-label="İlk sayfa"
        >
          <AiOutlineDoubleLeft className="text-lg" />
        </button>

        {/* Previous page button */}
        <button
          className={`px-3 py-2 rounded-lg transition-all ${
            currentPage <= 1
              ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
              : 'bg-primary text-white-100 hover:bg-opacity-80'
          }`}
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          aria-label="Önceki sayfa"
        >
          <AiOutlineLeft className="text-lg" />
        </button>

        {/* Show first page if not in visible range */}
        {pageNumbers[0] > 1 && (
          <>
            <button
              className="min-w-[40px] h-10 rounded-lg bg-primary text-white-100 hover:bg-opacity-80 transition-all"
              onClick={() => handlePageChange(1)}
            >
              1
            </button>
            {pageNumbers[0] > 2 && (
              <span className="px-2 text-gray-500">...</span>
            )}
          </>
        )}

        {/* Page numbers */}
        {pageNumbers.map((number) => (
          <button
            key={number}
            className={`min-w-[40px] h-10 rounded-lg transition-all font-medium ${
              currentPage === number
                ? 'bg-white text-gray-900 shadow-lg scale-110'
                : 'bg-primary text-white-100 hover:bg-opacity-80'
            }`}
            onClick={() => handlePageChange(number)}
          >
            {number}
          </button>
        ))}

        {/* Show last page if not in visible range */}
        {pageNumbers[pageNumbers.length - 1] < totalPages && (
          <>
            {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
              <span className="px-2 text-gray-500">...</span>
            )}
            <button
              className="min-w-[40px] h-10 rounded-lg bg-primary text-white-100 hover:bg-opacity-80 transition-all"
              onClick={() => handlePageChange(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}

        {/* Next page button */}
        <button
          className={`px-3 py-2 rounded-lg transition-all ${
            currentPage >= totalPages
              ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
              : 'bg-primary text-white-100 hover:bg-opacity-80'
          }`}
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          aria-label="Sonraki sayfa"
        >
          <AiOutlineRight className="text-lg" />
        </button>

        {/* Last page button */}
        <button
          className={`px-3 py-2 rounded-lg transition-all ${
            currentPage >= totalPages
              ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
              : 'bg-primary text-white-100 hover:bg-opacity-80'
          }`}
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage >= totalPages}
          aria-label="Son sayfa"
        >
          <AiOutlineDoubleRight className="text-lg" />
        </button>
      </div>

      {/* Page info for mobile */}
      <div className="text-sm text-gray-400 md:hidden">
        Sayfa <span className="text-white-100 font-medium">{currentPage}</span> / <span className="text-white-100 font-medium">{totalPages}</span>
      </div>
    </div>
  );
}

export default PaginationComponent;